import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Button,
  Checkbox,
  Indicators,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Textarea,
  Input,
} from 'ui'
import { useCurrentUser } from '../../../queries/useCurrentUser'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  useEvent,
  useGenerateEventDescriptionMutation,
  useUpdateEventMutation,
} from '../../../queries'
import { usePublishEvent } from '../../../queries/useEventOperations'
import { Wand2 } from 'lucide-react'
import toast from 'react-hot-toast'

// Validation schema
const schema = yup.object({
  title: yup.string().required('Cannot be blank'),
  description: yup.string().required('Cannot be blank'),
  agreeToTerms: yup
    .boolean()
    .required(
      'You must agree to the terms of the Educator PRO Connect platform'
    ),
})
export type EventStepFour = yup.InferType<typeof schema>

interface CreateEventStepFourFormProps {
  eventId: string
}

export const CreateEventStepFourForm: React.FC<
  CreateEventStepFourFormProps
> = ({ eventId }) => {
  const { data: user, isLoading: userLoading } = useCurrentUser()
  const isEducator = user?.userRole?.toLowerCase() === 'educator'
  const { data: event, isLoading: eventLoading } = useEvent(
    eventId !== 'new' ? eventId : ''
  )
  const generateDescription = useGenerateEventDescriptionMutation()
  const updateEvent = useUpdateEventMutation()
  const [publishEvent, { loading: publishLoading }] = usePublishEvent()
  const navigate = useNavigate()

  // Store original generated content for reset functionality
  const [originalTitle, setOriginalTitle] = useState('')
  const [originalDescription, setOriginalDescription] = useState('')

  // Track if auto-generation has been attempted to prevent double execution
  const hasAttemptedGeneration = useRef(false)

  const form = useForm<EventStepFour>({
    defaultValues: {
      title: event?.name || '',
      description: event?.description || '',
      agreeToTerms: false,
    } as EventStepFour,
    reValidateMode: 'onChange',
    resolver: yupResolver(schema as any) as any,
  })

  const isLoading = userLoading || eventLoading

  // Generate AI description only (title is preserved)
  const handleGenerateDescription = async () => {
    if (eventId === 'new') {
      toast.error(
        'Please complete the previous steps first to generate a description.'
      )
      return
    }

    // Prevent concurrent generation calls
    if (generateDescription.isPending) {
      return
    }

    try {
      const response = await generateDescription.mutateAsync({
        eventId,
      })

      if (response.success) {
        // Only update description - preserve the user's title
        form.setValue('description', response.description)

        // Store original description for reset functionality
        setOriginalDescription(response.description)

        toast.success('Generated event description successfully.')
      }
    } catch (error) {
      console.error('Generation error:', error)
    }
  }

  // Initialize form when event data is loaded
  useEffect(() => {
    if (event) {
      form.setValue('title', event.name || '')
      form.setValue('description', event.description || '')

      // Store original values for reset buttons
      setOriginalTitle(event.name || '')
      setOriginalDescription(event.description || '')

      // Auto-generate if description is empty/missing
      // Only attempt once per component mount to prevent double execution
      const hasDescription =
        event.description && event.description.trim().length > 0

      if (!hasDescription && !hasAttemptedGeneration.current) {
        hasAttemptedGeneration.current = true
        handleGenerateDescription()
      }
    }
  }, [event, form])

  // Reset handlers
  const handleResetTitle = () => {
    form.setValue('title', originalTitle)
  }

  const handleResetDescription = () => {
    form.setValue('description', originalDescription)
  }

  const handleSubmitForm =
    (redirect: string = '/app/events/{id}/matches') =>
    async (values: EventStepFour) => {
      try {
        // First, update the event with title and description
        await updateEvent.mutateAsync({
          id: eventId,
          name: values.title,
          description: values.description,
          status: 'DRAFT', // Always start as DRAFT
        })

        // For EDUCATORS: publish the event if they agreed to terms
        if (isEducator && values.agreeToTerms) {
          await publishEvent({ variables: { eventId } })
        }

        // For PROFESSIONALS: event remains in DRAFT status (already set above)
        // Admin will need to manually publish it

        // Navigate to matches view
        const targetRedirect = redirect.includes('{id}')
          ? redirect.replace('{id}', eventId)
          : redirect

        navigate(targetRedirect)
      } catch (error) {
        console.error('Update/publish event error:', error)
        toast.error('Failed to update event.')
      }
    }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmitForm())}
        className={clsx(isLoading && 'opacity-50')}
      >
        <div className="flex flex-col">
          <FormLabel>
            Here's how your event will appear to potential matches.
          </FormLabel>
          <p className="font-body text-sm mt-1">
            Based on everything you've told us in the previous steps, we've
            created a brief description that will appeal to busy{' '}
            {isEducator ? 'professionals' : 'educators'}. Feel free to add,
            edit, or start from scratch
          </p>

          <div
            className={clsx(
              'flex flex-col mt-4 bg-gradient-to-b from-white to-primary/5 p-4 rounded-lg border-primary/10 border p-6 relative',
              generateDescription.isPending && 'opacity-50'
            )}
          >
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="w-full col-span-3">
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {originalTitle && form.watch('title') !== originalTitle && (
              <Button
                type="button"
                variant="link"
                className="text-actionable underline text-sm py-4 mt-2 mb-6"
                onClick={handleResetTitle}
              >
                <span>Reset to original title</span>
              </Button>
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem className="w-full col-span-3">
                  <FormLabel>Event Description</FormLabel>
                  <FormControl>
                    <Textarea {...field} className="min-h-[150px]" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {originalDescription &&
              form.watch('description') !== originalDescription && (
                <Button
                  type="button"
                  variant="link"
                  className="text-actionable underline text-sm py-4 mt-2"
                  onClick={handleResetDescription}
                >
                  <span>Reset to original description</span>
                </Button>
              )}
            <div className="flex justify-end mt-4">
              <Button
                type="button"
                size="sm"
                className="text-sm flex items-center gap-2"
                onClick={handleGenerateDescription}
                disabled={generateDescription.isPending || eventId === 'new'}
              >
                {generateDescription.isPending ? (
                  <Indicators.Spinner size="sm" />
                ) : (
                  <Wand2 className="h-4 w-4" />
                )}
                <span>Generate with AI</span>
              </Button>
            </div>
          </div>
        </div>

        <FormLabel className="mt-8 block">
          {isEducator
            ? 'Ready to publish this event and invite your matches?'
            : 'Ready to submit this event for approval?'}
        </FormLabel>

        <p className="font-body text-sm mt-2 mb-6">
          {isEducator ? (
            <>
              Simply agree to the following and we'll show you the matches!{' '}
              <span className="underline font-bold">
                Your event will be published immediately and matches will be
                visible to you.
              </span>
            </>
          ) : (
            <>
              Simply agree to the following and we'll submit your event for
              admin approval.{' '}
              <span className="underline font-bold">
                Note that your event will remain in draft mode and matches will
                not be visible until it's approved and published by an admin!
              </span>
            </>
          )}
        </p>

        <FormField
          control={form.control}
          name="agreeToTerms"
          render={({ field }) => (
            <div className="mt-2">
              <FormItem className="flex items-start space-x-2">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    className="mt-1"
                  />
                </FormControl>
                <FormLabel className="text-sm ml-1 text-body text-body/70">
                  I agree that my event abides by the terms of the Educator PRO
                  Connect platform. I am creating this event for education
                  purposes to connect{' '}
                  {isEducator
                    ? 'industry professionals to support students and/or educators'
                    : 'with educators to support students and educational goals'}{' '}
                  for education purposes. I will correspond with the platform's{' '}
                  {isEducator ? 'industry professionals' : 'educators'} with
                  accuracy, completeness, and timeliness.
                </FormLabel>
              </FormItem>
            </div>
          )}
        />

        <div className="flex-col flex mt-12 w-full">
          <Button
            type="submit"
            disabled={
              !form.watch('agreeToTerms') ||
              form.formState.isSubmitting ||
              publishLoading ||
              isLoading
            }
            variant="default"
            className="mx-auto uppercase"
          >
            {form.formState.isSubmitting ||
            updateEvent.isPending ||
            publishLoading ? (
              <Indicators.Spinner
                size="lg"
                className="mt-auto mr-2 mb-auto ml-auto"
              />
            ) : null}
            <span>
              {isEducator ? 'Publish & View Matches >' : 'Submit for Approval >'}
            </span>
          </Button>
          {!form.watch('agreeToTerms') && (
            <p className="text-center text-body/70 font-body text-xs mt-2">
              You must agree to the terms to continue
            </p>
          )}
        </div>
      </form>
    </Form>
  )
}
